import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { i18n } from "../i18n";
import { isLocale } from "../i18n/locale";
import { api, onAccessTokenChange, refreshSession, setAccessToken } from "../lib/api";
import { track } from "../lib/gtm";
import type {
  AuthResult,
  LoginOutcome,
  MemberRole,
  Organization,
  RegisterOutcome,
  Session,
  TotpChallenge,
  User,
  VerificationPending,
} from "../lib/types";

const ORG_STORAGE_KEY = "maxlayer.orgId";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: Status;
  user: User | null;
  organizations: Organization[];
  currentOrg: Organization | null;
  role: MemberRole | null;
  selectOrg: (orgId: string) => void;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** Completes a challenged login with the second factor. */
  verifyTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  /** Trades the one-time code a social sign-in landed with for a session, or a challenge. */
  exchangeOAuth: (code: string) => Promise<LoginOutcome & { created?: boolean }>;
  /**
   * Creates the account, and answers which of the two things happened.
   *
   * `authenticated` is a deployment with no email configured: registration signs
   * you straight in, as it always did. `verification-pending` is one that can
   * send mail, where the account exists and cannot be used until the address is
   * confirmed — there is no session to apply, so the caller shows the
   * check-your-inbox screen instead of navigating.
   *
   * Returned rather than thrown, because pending verification is the successful
   * path on any real deployment and an exception is a poor way to say "this
   * worked".
   */
  register: (input: {
    name: string;
    email: string;
    password: string;
    organizationName: string;
    /** Optional, and parked until the address is confirmed. */
    promoCode?: string;
    /**
     * Whether this signup is applying for free inference.
     *
     * Parked like `promoCode` and for the same reason: the request reaches an
     * operator's queue when the address is confirmed, so a throwaway signup
     * cannot take a place in it. It is asked here because the programme is for
     * new accounts, and this is the only screen where that is not a question.
     */
    freeInference?: boolean;
    /**
     * Optional, and *not* parked — attribution is recorded at registration.
     *
     * The fallback path for an affiliate referral, used where the marketing
     * site's cookie was declined; the API prefers its own cookie where both
     * arrive. See `referralFromQuery` in `RegisterPage`.
     */
    referralCode?: string;
  }) => Promise<RegisterOutcome>;
  logout: () => Promise<void>;
  reloadSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string | null>(() =>
    localStorage.getItem(ORG_STORAGE_KEY),
  );

  const loadSession = useCallback(async () => {
    const session = await api<Session>("/v1/auth/me");
    if (isLocale(session.user?.locale) && session.user.locale !== i18n.language) {
      await i18n.changeLanguage(session.user.locale);
    }
    setUser(session.user);
    setOrganizations(session.organizations);
    setStatus("authenticated");

    // Fall back to the first org whenever the stored one is gone.
    setOrgId((current) => {
      const stillValid = current && session.organizations.some((org) => org.id === current);
      const next = stillValid ? current : (session.organizations[0]?.id ?? null);
      if (next) localStorage.setItem(ORG_STORAGE_KEY, next);
      else localStorage.removeItem(ORG_STORAGE_KEY);
      return next;
    });
  }, []);

  // On boot the access token is gone but the refresh cookie may not be.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = await refreshSession();
      if (cancelled) return;
      if (!token) {
        setStatus("anonymous");
        return;
      }
      try {
        await loadSession();
      } catch {
        if (!cancelled) setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  /**
   * A refresh that fails clears the token from under us. Without this the app
   * stays "authenticated" over a session that no longer exists, and every panel
   * renders its own 401 instead of the login form.
   *
   * Only the null direction is handled here: arriving at a token is what
   * `loadSession` is for, and it has organizations to fetch first.
   */
  useEffect(
    () =>
      onAccessTokenChange((token) => {
        if (token !== null) return;
        setUser(null);
        setOrganizations([]);
        setStatus("anonymous");
      }),
    [],
  );

  const applyAuthResult = useCallback(
    async (result: AuthResult) => {
      setAccessToken(result.accessToken);
      setUser(result.user);
      await loadSession();
    },
    [loadSession],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const result = await api<AuthResult | TotpChallenge>("/v1/auth/login", {
        method: "POST",
        body: { email, password },
        anonymous: true,
      });
      if ("challenge" in result) {
        /*
         * A challenge is not a session yet, and counting it as one would make
         * every account with a second factor look like it signs in twice. The
         * event that follows a passed challenge is pushed by `verifyTwoFactor`.
         */
        track("login_challenge", { object: "account", detail: "2fa" });
        return { kind: "challenge", challengeToken: result.challengeToken, expiresIn: result.expiresIn };
      }
      await applyAuthResult(result);
      track("login", { object: "account", detail: "password" });
      return { kind: "authenticated" };
    },
    [applyAuthResult],
  );

  const exchangeOAuth = useCallback(
    async (code: string): Promise<LoginOutcome & { created?: boolean }> => {
      const result = await api<(AuthResult | TotpChallenge) & { created?: boolean; provider?: string }>("/v1/auth/oauth/exchange", {
        method: "POST",
        body: { code },
        anonymous: true,
      });
      if ("challenge" in result) {
        track("login_challenge", { object: "account", detail: "2fa" });
        return { kind: "challenge", challengeToken: result.challengeToken, expiresIn: result.expiresIn, created: result.created };
      }
      await applyAuthResult(result);
      track(result.created ? "register" : "login", { object: "account", detail: result.provider ?? "oauth" });
      return { kind: "authenticated", created: result.created };
    },
    [applyAuthResult],
  );

  const verifyTwoFactor = useCallback(
    async (challengeToken: string, code: string) => {
      const result = await api<AuthResult>("/v1/auth/2fa/verify", {
        method: "POST",
        body: { challengeToken, code },
        anonymous: true,
      });
      await applyAuthResult(result);
      track("login", { object: "account", detail: "2fa" });
    },
    [applyAuthResult],
  );

  const register = useCallback(
    async (input: {
      name: string;
      email: string;
      password: string;
      organizationName: string;
      promoCode?: string;
      freeInference?: boolean;
      referralCode?: string;
      turnstileToken?: string;
    }): Promise<RegisterOutcome> => {
      const result = await api<AuthResult | VerificationPending>("/v1/auth/register", {
        method: "POST",
        body: { ...input, locale: i18n.language },
        anonymous: true,
      });

      /*
       * No `accessToken` means the API is holding the account until the address
       * is confirmed. Discriminated on the field the response either has or has
       * not, rather than on the status code, because `api` does not surface one
       * and a 201/202 distinction is a weaker contract than a missing token.
       */
      if ("verificationRequired" in result) {
        /*
         * An account exists either way, so both branches are a sign-up — the
         * detail is what separates the ones that can use the product now from
         * the ones waiting on an email that may not have gone out.
         */
        track("sign_up", { object: "account", detail: "verification-pending" });
        return {
          kind: "verification-pending",
          email: result.email,
          /*
           * Carried through so the screen can say something true when the mail
           * did not go out. Telling somebody to check their inbox for a message
           * the platform knows it failed to send is how a signup silently ends.
           */
          emailSent: result.emailSent,
        };
      }

      await applyAuthResult(result);
      track("sign_up", { object: "account", detail: "immediate" });
      return { kind: "authenticated" };
    },
    [applyAuthResult],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>("/v1/auth/logout", { method: "POST" });
    } finally {
      track("logout", { object: "account" });
      setAccessToken(null);
      setUser(null);
      setOrganizations([]);
      setOrgId(null);
      localStorage.removeItem(ORG_STORAGE_KEY);
      setStatus("anonymous");
    }
  }, []);

  const selectOrg = useCallback((next: string) => {
    localStorage.setItem(ORG_STORAGE_KEY, next);
    setOrgId(next);
  }, []);

  const currentOrg = useMemo(
    () => organizations.find((org) => org.id === orgId) ?? organizations[0] ?? null,
    [organizations, orgId],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      organizations,
      currentOrg,
      role: currentOrg?.role ?? null,
      selectOrg,
      login,
      verifyTwoFactor,
      exchangeOAuth,
      register,
      logout,
      reloadSession: loadSession,
    }),
    [status, user, organizations, currentOrg, selectOrg, login, verifyTwoFactor, exchangeOAuth, register, logout, loadSession],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}

const RANK: Record<MemberRole, number> = { viewer: 0, developer: 1, admin: 2, owner: 3 };

export function useCan(required: MemberRole): boolean {
  const { role } = useAuth();
  return role !== null && RANK[role] >= RANK[required];
}
